/**
 * Slide Guide Generator Service
 * 
 * Generates visual blueprints/wireframes using Google Gemini before ExecutionAgent builds.
 * This gives the execution agent a clear visual target to work toward.
 * 
 * The actual Gemini API calls happen on the Python backend (keeps API key secure).
 * This service handles:
 * 1. Building the comprehensive prompt from section context
 * 2. Collecting reference images
 * 3. Calling the backend endpoint
 * 4. Processing and caching the result
 * 
 * Based on SlideGuideGenerator from big-slide-gen.
 */

import { backendClient } from "../../../../services/communication/backendClient";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface SlideGuideRequest {
    /** Instructions for what to build on this slide */
    instructions: string;
    
    /** Master template data */
    masterData: {
        contentArea?: {
            left: number;
            top: number;
            width: number;
            height: number;
        };
        exclusionZones?: Array<{
            type: string;
            bounds: {
                left: number;
                top: number;
                width: number;
                height: number;
            };
        }>;
        slideDimensions?: {
            width: number;
            height: number;
        };
    };
    
    /** Reference slide images (base64) */
    referenceImages: string[];
    
    /** Style patterns from the presentation */
    stylePatterns?: {
        fonts?: Array<{ family: string; size: number }>;
        colors?: Array<{ hex?: string; rgb?: string }>;
        topStyles?: Array<{
            fontFamily?: string;
            fontSize?: number;
            fontColor?: string;
            usagePercentage?: number;
        }>;
    };
}

export interface SlideGuideResponse {
    success: boolean;
    /** Generated blueprint image as base64 */
    imageBase64?: string;
    /** The prompt that was used (for debugging) */
    promptUsed?: string;
    /** Additional metadata */
    metadata?: {
        model?: string;
        referenceImagesUsed?: number;
        guideSavedTo?: string;
        textResponse?: string;
    };
    error?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// SLIDE GUIDE GENERATOR
// ═══════════════════════════════════════════════════════════════════════════

export class SlideGuideGenerator {
    private planId: string;
    
    constructor(planId: string) {
        this.planId = planId;
    }
    
    /**
     * Generate a visual blueprint for a slide section
     * 
     * @param request - The guide generation request
     * @returns Generated blueprint image and metadata
     */
    async generateGuide(request: SlideGuideRequest): Promise<SlideGuideResponse> {
        console.log(`[SlideGuideGenerator] Generating guide for plan ${this.planId}`);
        console.log(`[SlideGuideGenerator] Instructions: ${request.instructions.substring(0, 100)}...`);
        console.log(`[SlideGuideGenerator] Reference images: ${request.referenceImages.length}`);
        
        try {
            // Call the backend endpoint
            const fetchResponse = await fetch(`${backendClient.baseUrl}/plans/${this.planId}/build/generate-guide`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    instructions: request.instructions,
                    master_data: {
                        content_area: request.masterData.contentArea,
                        exclusion_zones: request.masterData.exclusionZones,
                        slide_dimensions: request.masterData.slideDimensions,
                    },
                    reference_images: request.referenceImages,
                    style_patterns: request.stylePatterns ? {
                        fonts: request.stylePatterns.fonts,
                        colors: request.stylePatterns.colors,
                    } : {},
                }),
            });
            
            if (!fetchResponse.ok) {
                const errorText = await fetchResponse.text();
                console.log(`[SlideGuideGenerator] Guide generation failed: ${errorText}`);
                return {
                    success: false,
                    error: errorText || 'Unknown error',
                };
            }
            
            const response = await fetchResponse.json() as {
                success: boolean;
                image_base64?: string;
                prompt_used?: string;
                metadata?: Record<string, unknown>;
                error?: string;
            };
            
            if (!response.success) {
                console.log(`[SlideGuideGenerator] Guide generation failed: ${response.error}`);
                return {
                    success: false,
                    error: response.error || 'Unknown error',
                };
            }
            
            console.log(`[SlideGuideGenerator] Guide generated successfully`);
            
            return {
                success: true,
                imageBase64: response.image_base64,
                promptUsed: response.prompt_used,
                metadata: {
                    model: response.metadata?.model as string,
                    referenceImagesUsed: response.metadata?.reference_images_used as number,
                    guideSavedTo: response.metadata?.guide_saved_to as string,
                    textResponse: response.metadata?.text_response as string,
                },
            };
            
        } catch (error) {
            console.error(`[SlideGuideGenerator] Error generating guide:`, error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }
    
    /**
     * Build comprehensive instructions for a slide from section context
     * 
     * This combines the slide purpose with any additional context to create
     * detailed instructions for the visual guide.
     */
    static buildInstructionsFromContext(
        slideTitle: string,
        slidePurpose: string,
        contentType: string,
        additionalContext?: string[]
    ): string {
        const lines: string[] = [];
        
        lines.push(`Slide Title: ${slideTitle}`);
        lines.push(`Purpose: ${slidePurpose}`);
        lines.push(`Primary Content Type: ${contentType}`);
        lines.push('');
        
        // Add content type specific guidance
        switch (contentType) {
            case 'chart':
                lines.push('This slide should feature a sophisticated chart as the primary element.');
                lines.push('Include: data labels, reference lines, annotations, and source footnotes.');
                break;
            case 'table':
                lines.push('This slide should feature a detailed table as the primary element.');
                lines.push('Include: nested headers, proper border hierarchy, and formatted data cells.');
                break;
            case 'text':
                lines.push('This slide should focus on text content with clear hierarchy.');
                lines.push('Include: compelling headline, well-structured bullet points, supporting footnotes.');
                break;
            case 'mixed':
                lines.push('This slide should combine multiple content types effectively.');
                lines.push('Include: balanced layout with charts/tables and supporting text elements.');
                break;
        }
        
        if (additionalContext && additionalContext.length > 0) {
            lines.push('');
            lines.push('Additional Context:');
            for (const ctx of additionalContext) {
                lines.push(`- ${ctx}`);
            }
        }
        
        return lines.join('\n');
    }
    
    /**
     * Extract reference images from search results
     * 
     * Converts search result slide images to base64 for the guide generator.
     */
    static extractReferenceImages(
        searchResults: Array<{ screenshot?: string; slideImage?: string }>
    ): string[] {
        const images: string[] = [];
        
        for (const result of searchResults) {
            const image = result.screenshot || result.slideImage;
            if (image) {
                // Handle both data URL and raw base64
                if (image.startsWith('data:')) {
                    images.push(image);
                } else {
                    images.push(`data:image/png;base64,${image}`);
                }
            }
        }
        
        // Limit to 5 reference images to avoid overwhelming the model
        return images.slice(0, 5);
    }
    
    /**
     * Convert style guide overview to style patterns for the generator
     */
    static convertStyleGuideToPatterns(
        styleGuideOverview?: {
            topStyleSignatures?: Array<{
                style?: {
                    font_family?: string;
                    font_size?: number;
                    font_color?: string;
                    fill_color?: string;
                };
                usage_percentage?: number;
            }>;
        }
    ): SlideGuideRequest['stylePatterns'] {
        if (!styleGuideOverview?.topStyleSignatures) {
            return undefined;
        }
        
        const fonts: Array<{ family: string; size: number }> = [];
        const colors: Array<{ hex?: string; rgb?: string }> = [];
        const seenFonts = new Set<string>();
        const seenColors = new Set<string>();
        
        for (const sig of styleGuideOverview.topStyleSignatures) {
            const style = sig.style || {};
            
            // Extract font
            if (style.font_family && style.font_size) {
                const fontKey = `${style.font_family}-${style.font_size}`;
                if (!seenFonts.has(fontKey)) {
                    seenFonts.add(fontKey);
                    fonts.push({
                        family: style.font_family,
                        size: style.font_size,
                    });
                }
            }
            
            // Extract colors
            if (style.font_color && !seenColors.has(style.font_color)) {
                seenColors.add(style.font_color);
                colors.push({ hex: style.font_color });
            }
            if (style.fill_color && !seenColors.has(style.fill_color)) {
                seenColors.add(style.fill_color);
                colors.push({ hex: style.fill_color });
            }
        }
        
        return {
            fonts,
            colors,
            topStyles: styleGuideOverview.topStyleSignatures.map(sig => ({
                fontFamily: sig.style?.font_family,
                fontSize: sig.style?.font_size,
                fontColor: sig.style?.font_color,
                usagePercentage: sig.usage_percentage,
            })),
        };
    }
}

export default SlideGuideGenerator;
